"use client"

import { CSSProperties, useEffect, useRef, useState } from "react"

import {
  TextStagger,
  textStaggerGroupClass,
} from "@modules/products/components/text-stagger"

import RelatedProductCard from "./card"
import { RelatedProductCardData } from "./card-data"
import styles from "./related-products.module.css"

const VISIBLE_CARDS = 4

type RelatedProductsCarouselProps = {
  products: RelatedProductCardData[]
  title?: string
}

function ChevronIcon({ direction }: { direction: "prev" | "next" }) {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24">
      <path d={direction === "prev" ? "m14.5 5-7 7 7 7" : "m9.5 5 7 7-7 7"} />
    </svg>
  )
}

export default function RelatedProductsCarousel({
  products,
  title,
}: RelatedProductsCarouselProps) {
  const [index, setIndex] = useState(0)
  const [revealed, setRevealed] = useState(false)
  const sectionRef = useRef<HTMLElement>(null)
  const maxIndex = Math.max(products.length - VISIBLE_CARDS, 0)

  useEffect(() => {
    const section = sectionRef.current

    if (!section) {
      return
    }

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setRevealed(true)
      return
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting) {
          return
        }

        setRevealed(true)
        observer.disconnect()
      },
      { rootMargin: "0px 0px -12% 0px", threshold: 0.01 }
    )

    observer.observe(section)

    return () => observer.disconnect()
  }, [])

  return (
    <section
      className={`${styles.carousel}${revealed ? ` ${textStaggerGroupClass}` : ""}`}
      data-testid="related-products-carousel"
      ref={sectionRef}
    >
      {title && (
        <TextStagger index={0}>
          <h2 className={styles.title}>{title}</h2>
        </TextStagger>
      )}

      <div className={styles.viewport}>
        <button
          aria-label="Prodotti precedenti"
          className={`${styles.arrow} ${styles.arrowPrev}`}
          disabled={index === 0}
          onClick={() => setIndex((current) => Math.max(current - 1, 0))}
          type="button"
        >
          <ChevronIcon direction="prev" />
        </button>

        <div className={styles.window}>
          <ul
            className={styles.track}
            style={{ "--rp-index": index } as CSSProperties}
          >
            {products.map((product, productIndex) => (
              <li
                className={styles.item}
                inert={
                  productIndex < index || productIndex >= index + VISIBLE_CARDS
                }
                key={product.id}
              >
                <RelatedProductCard product={product} />
              </li>
            ))}
          </ul>
        </div>

        <button
          aria-label="Prodotti successivi"
          className={`${styles.arrow} ${styles.arrowNext}`}
          disabled={index === maxIndex}
          onClick={() => setIndex((current) => Math.min(current + 1, maxIndex))}
          type="button"
        >
          <ChevronIcon direction="next" />
        </button>
      </div>
    </section>
  )
}
